
import clsx from "clsx";
import bronze_checklist_icon from "../../../assets/bronze_checklist.webp";
import type { JSX } from "react";
import { Alert } from "../../ui/Alert.tsx";

interface InstructionsProps {
  labType: "dry" | "wet";
  className?: string;
  tier: "bronze" | "silver" | "gold";
  auditorMode?: boolean;
  isAuditorMode: boolean;
  isActualAuditor?: boolean;
}

// Tier color configurations
const tierStyles = {
  bronze: {
    titleColor: 'text-[#bc7f48]',
    accentColor: 'text-[#57be82]',
    auditorTitleColor: 'text-[#3b4b32]',
    auditorAccentColor: 'text-[#3b4b32]',
  },
  silver: {
    titleColor: 'text-[#6E6E6E]',
    accentColor: 'text-[#9E9E9E]',
    auditorTitleColor: 'text-[#4A4A4A]',
    auditorAccentColor: 'text-[#4A4A4A]',
  },
  gold: {
    titleColor: 'text-[#D4A017]',
    accentColor: 'text-[#A68312]',
    auditorTitleColor: 'text-[#8B7012]',
    auditorAccentColor: 'text-[#8B7012]',
  },
};

export const Instructions = ({
  labType,
  className,
  tier,
  auditorMode,
  isAuditorMode,
  isActualAuditor = false,
}: InstructionsProps) => {
  const styles = tierStyles[tier] || tierStyles.bronze;
  const titleColor = isAuditorMode ? styles.auditorTitleColor : styles.titleColor;
  const accentColor = isAuditorMode ? styles.auditorAccentColor : styles.accentColor;

  return (
    <div className={clsx("card shadow-sm w-fit mx-auto", "bg-amber-50", className)}>
      <div className="card-body flex gap-4 flex-col">
        <div className="flex items-center not-prose gap-4">
          <img width={50} src={getInstructionsTitle(isAuditorMode)[tier]?.icon} />
          <h2 className={`font-bold text-[41px] ${titleColor}`}>
            {getInstructionsTitle(isAuditorMode)[tier]?.title}
          </h2>
        </div>
        <div className="pl-[66px]">
          <b className={accentColor}>Purpose:</b>
          <span>{getPurposeCopy(isAuditorMode, isActualAuditor)[labType]}</span>
        </div>
        <div className="pl-[66px]">
          <b className={accentColor}>Focus Area:</b>
          <span>{getFocusAreaCopy(tier)[labType]}</span>
        </div>
        <div className="pl-[66px]">
          <b className={accentColor}>General Instruction:</b>
          <span>{getGeneralInstructionsCopy(isAuditorMode, isActualAuditor, tier)[labType]}</span>
        </div>
        <div className={`text-[30px] ${accentColor} font-bold pl-[66px]`}>
          {isAuditorMode ? (isActualAuditor ? 'Are you with the Lab Champion? If yes then let\'s start.' : 'Review the auditor checklist items below.') : 'Are you ready? Let\'s start!'}
        </div>

        {auditorMode && isActualAuditor && (
          <Alert className={"max-w-1/2"} soft variant={"info"}>Ensure that you're with the lab champion before you begin!</Alert>
        )}
      </div>
    </div>
  );
};

const getInstructionsTitle = (isAuditorMode: boolean): Record<
  string,
  { title: string; color: string; icon: string }
> => ({
  bronze: {
    title: `Bronze Certification - ${isAuditorMode ? 'Auditor Checklist' : 'Actions Checklist'}`,
    color: "var(--l0-brown)",
    icon: bronze_checklist_icon,
  },
  silver: {
    title: `Silver Certification - ${isAuditorMode ? 'Auditor Checklist' : 'Actions Checklist'}`,
    color: "var(--l0-silver)",
    icon: bronze_checklist_icon, // Placeholder - reuse bronze for now
  },
  gold: {
    title: `Gold Certification - ${isAuditorMode ? 'Auditor Checklist' : 'Actions Checklist'}`,
    color: "var(--l0-gold)",
    icon: bronze_checklist_icon, // Placeholder - reuse bronze for now
  },
});

const getPurposeCopy = (isAuditorMode: boolean, isActualAuditor: boolean): Record<"dry" | "wet", JSX.Element> => ({
  dry: <p>{isAuditorMode ? (isActualAuditor ? 'Review and verify a dry lab\'s submitted actions to ensure accuracy and award certification.' : 'View the auditor checklist items to understand what will be assessed during the audit.') : 'Encourage discussion and foster shared responsibility.'}</p>,
  wet: <p>{isAuditorMode ? (isActualAuditor ? 'Review and verify a wet lab\'s submitted actions to ensure accuracy and award certification.' : 'View the auditor checklist items to understand what will be assessed during the audit.') : 'Encourage discussion and foster shared responsibility.'}</p>,
});

// Tier-specific focus areas
const getFocusAreaCopy = (tier: "bronze" | "silver" | "gold"): Record<"dry" | "wet", JSX.Element> => {
  const focusAreas = {
    bronze: {
      dry: (
        <ol>
          <li>Green culture & team communication</li>
          <li>Computer energy use</li>
          <li>Eco-Office</li>
          <li>Data storage</li>
        </ol>
      ),
      wet: (
        <ol>
          <li>Green culture & team communication</li>
          <li>Lab waste management</li>
          <li>Cold storage</li>
          <li>Computer & laptop energy use</li>
        </ol>
      ),
    },
    silver: {
      dry: (
        <ol>
          <li>Sustainable transport</li>
          <li>Procurement</li>
          <li>Green chemistry</li>
        </ol>
      ),
      wet: (
        <ol>
          <li>Sustainable transport</li>
          <li>Procurement</li>
          <li>Green chemistry</li>
        </ol>
      ),
    },
    gold: {
      dry: (
        <ol>
          <li>Eco offices</li>
          <li>Equipment</li>
          <li>Water</li>
        </ol>
      ),
      wet: (
        <ol>
          <li>Eco offices</li>
          <li>Equipment</li>
          <li>Water</li>
        </ol>
      ),
    },
  };

  return focusAreas[tier] || focusAreas.bronze;
};

const getGeneralInstructionsCopy = (isAuditorMode: boolean, isActualAuditor: boolean, tier: "bronze" | "silver" | "gold"): Record<"dry" | "wet", JSX.Element> => {
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);

  return {
    dry: (
      isAuditorMode ? (isActualAuditor ? <ul className="list-disc">
        <li>Complete this during an audit or walkthrough with the LAB ZER0 Champion or lab team.</li>
        <li>
          Focus on evidence-based checks and open discussion where needed.
        </li>
        <li>
          The lab must pass at least 80% to successfully be awarded {tierLabel.toLowerCase()} certification.
        </li>
        <li>
          If this is not obtained please select 'pending re-submission'.
        </li>
      </ul>
        :
        <ul className="list-disc">
          <li>This is a read-only view of the auditor checklist.</li>
          <li>You can see what items will be assessed during the audit.</li>
          <li>Only auditors can interact with and complete this checklist.</li>
        </ul>)
        :
        <ul className="list-disc">
          <li>Complete this as a lab group or with your LAB ZER0 Champion</li>
          <li>
            All questions are Yes/No or Optional N/A with "Comments" field at the
            end.
          </li>
          <li>
            The lab must complete at least 80% of the actions to submit and
            proceed to the next step.
          </li>
        </ul>
    ),
    wet: (
      isAuditorMode ? (isActualAuditor ? <ul className="list-disc">
        <li>Complete this during an audit or walkthrough with the LAB ZER0 Champion or lab team.</li>
        <li>
          Focus on evidence-based checks and open discussion where needed.

        </li>
        <li>
          The lab must pass at least 80% to successfully be awarded {tierLabel.toLowerCase()} certification.
        </li>
        <li>
          If this is not obtained please select 'pending re-submission'.
        </li>
      </ul>
        :
        <ul className="list-disc">
          <li>This is a read-only view of the auditor checklist.</li>
          <li>You can see what items will be assessed during the audit.</li>
          <li>Only auditors can interact with and complete this checklist.</li>
        </ul>)
        :
        <ul className="list-disc">
          <li>Complete this as a lab group or with your LAB ZER0 Champion</li>
          <li>
            All questions are Yes/No or Optional N/A with "Comments" field at the
            end.
          </li>
          <li>
            The lab must complete at least 80% of the actions to submit and
            proceed to the next step.
          </li>
        </ul>
    ),
  };
};